import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  NgbActiveModal,
  NgbDateAdapter,
  NgbDateNativeAdapter,
  NgbDateParserFormatter
} from '@ng-bootstrap/ng-bootstrap';
import { isEmpty } from 'lodash';
import { from } from 'rxjs';
import { CanadianWorkersCompensationForm } from 'src/app/classes/form-groups/canadian-workers-compensation-form';
import { CompanyVendorFile } from 'src/app/classes/models';
import { CustomNgbDateFormatter } from 'src/app/dependencies/custom-ngb-date-formatter';
import { CanadianWorkersCompensationType } from 'src/app/interfaces/canadian-workers-compensation-type';
import { ComplianceService } from 'src/app/services/compliance.service';
import { FileService } from 'src/app/services/file.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';

@Component({
  selector: 'app-canadian-workers-compensation-modal',
  templateUrl: './canadian-workers-compensation-modal.component.html',
  styleUrls: ['./canadian-workers-compensation-modal.component.less'],
  providers: [
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CanadianWorkersCompensationModalComponent implements OnInit {
  constructor(
    private ActiveModal: NgbActiveModal,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ComplianceService: ComplianceService,
    private FileService: FileService
  ) {}

  @Input()
  CanadianWorkersCompensationFormGroup!: CanadianWorkersCompensationForm;
  @Input() CompanyVendorId!: number;
  @Input() CompanyId!: number;
  @Input() VendorId!: number;
  @Input() CanadianWorkersCompensationType!: CanadianWorkersCompensationType;
  @Input() WSIBCertificateImageFile?: CompanyVendorFile;

  LookupUrlMap = new Map<CanadianWorkersCompensationType, string>([
    ['WSIB', 'https://clearances.wsib.ca/Clearances/eclearance/start?lang=en'],
    ['WCB', 'https://decc.wcb.ab.ca/Clearance/RequestClearance.aspx']
  ]);

  Statuses: { label: string; value: string }[] = [
    { label: 'Eligible', value: 'Eligible' },
    { label: 'Ineligible', value: 'Ineligible' },
    { label: 'Preliminary', value: 'Preliminary' }
  ];

  openCAWCLookup() {
    const url = this.LookupUrlMap.get(this.CanadianWorkersCompensationType);

    if (!url) {
      throw this.ToastService.addErrorToast('Error', 'Missing lookup url.', {
        returnError: true
      });
    }

    window.open(url, '_blank');
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.CanadianWorkersCompensationFormGroup
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with the form below.',
        { returnError: true }
      );
    }

    const {
      type,
      CAWCNumber,
      CAWCcertNumber,
      CAWCExempt,
      CAWCStatus,
      CAWCDate
    } = this.CanadianWorkersCompensationFormGroup.value;

    from(
      companyVendorsService.upsertCanadianWorkersCompensation({
        pathParams: { companyId: this.CompanyId, vendorId: this.VendorId },
        data: {
          records: [
            {
              type: type!,
              CAWCNumber,
              CAWCcertNumber,
              CAWCExempt,
              CAWCStatus,
              CAWCDate: CAWCDate ? new Date(CAWCDate).toISOString() : CAWCDate
            }
          ]
        }
      })
    ).subscribe({
      next: (SavedRecords) => {
        this.ActiveModal.close(
          SavedRecords.find(
            ({ type }) => type === this.CanadianWorkersCompensationType
          )
        );
        this.ToastService.addSuccessToast(
          'Success',
          "Successfully saved Canadian worker's compensation"
        );
      }
    });
  }

  close() {
    this.ActiveModal.dismiss();
  }

  displayWSIBCertificateImage() {
    if (!this.WSIBCertificateImageFile) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing WSIB certificate image.',
        { returnError: true }
      );
    }

    const { CompanyVendorId, CompanyId, VendorId } =
      this.WSIBCertificateImageFile;

    this.FileService.getCompanyVendorFile({
      CompanyVendorId: CompanyVendorId,
      CompanyId: CompanyId,
      VendorId: VendorId,
      docType: 'WSIB',
      File: JSON.stringify({}),
      order: JSON.stringify([['createdAt', 'DESC']])
    }).subscribe({
      next: (CompanyVendorFile) => {
        const { id: CompanyVendorFileId, File } = CompanyVendorFile;

        if (!File) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Client/Vendor file is missing file information.',
            { returnError: true }
          );
        }

        const { filetype, filepath } = File;

        if (!filepath) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No file path found.',
            { returnError: true }
          );
        }

        this.FileService.getCompanyVendorFileS3PresignedUrl({
          id: CompanyVendorFileId
        }).subscribe({
          next: ({ presignedUrl, filename }) => {
            from(fetch(presignedUrl)).subscribe({
              next: (response) => {
                from(response.arrayBuffer()).subscribe({
                  next: (arrayBuffer) => {
                    const svgImage = new TextDecoder('utf-8').decode(
                      arrayBuffer
                    );

                    const base64EncodedSVGImage = window.btoa(
                      `<svg xmlns="http://www.w3.org/2000/svg" height="200%" width="100%"><foreignObject width="100%" height="100%">${svgImage}</foreignObject></svg>`
                    );
                    const ImageArrayBuffer =
                      this.UtilityService.convertBase64StringToArrayBuffer(
                        base64EncodedSVGImage
                      );
                    const TypedArray = new Uint8Array(ImageArrayBuffer);
                    const DataBlob = new Blob([TypedArray], {
                      type: filetype
                    });
                    window.open(URL.createObjectURL(DataBlob), '_blank');
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  runCAWCLookup() {
    const {
      controls: {
        type: { value: type },
        CAWCcertNumber: { value: CAWCcertNumber },
        CAWCNumber: { value: CAWCNumber }
      }
    } = this.CanadianWorkersCompensationFormGroup;

    if (type !== 'WSIB') {
      throw this.ToastService.addErrorToast(
        'Error',
        'Can only run lookup for WSIB.',
        { returnError: true }
      );
    }

    if (!CAWCcertNumber && !CAWCNumber) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must add either an Account Number or a Certificate Number.',
        { returnError: true }
      );
    }

    const Parameters: {
      certificateSearchCriteria?: { certificateNumber: string };
      accountSearchCriteria?: { searchType: string; accountNumber: string };
      searchingFor?: string;
      accountSearchButtonSimple?: string;
    } = {};

    if (CAWCcertNumber) {
      Object.assign(Parameters, {
        certificateSearchCriteria: { certificateNumber: CAWCcertNumber }
      });
    }

    if (CAWCNumber) {
      Object.assign(Parameters, {
        accountSearchCriteria: {
          searchType: 'SIMPLE',
          accountNumber: CAWCNumber
        },
        searchingFor: 'contractors',
        accountSearchButtonSimple: 'Search'
      });
    }

    if (isEmpty(Parameters)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error setting lookup parameters.',
        { returnError: true }
      );
    }

    this.ComplianceService.wsibLookup(Parameters, {
      CompanyVendorId: this.CompanyVendorId,
      VendorId: this.VendorId,
      CompanyId: this.CompanyId
    }).subscribe({
      next: ({ wsibResponseData, CompanyVendorFile }) => {
        const { isCertInvalid, expirationDate, certNumber } = wsibResponseData;

        this.WSIBCertificateImageFile = CompanyVendorFile;

        if (isCertInvalid || !expirationDate) {
          this.CanadianWorkersCompensationFormGroup.patchValue({
            CAWCStatus: 'Ineligible',
            CAWCDate: null,
            CAWCcertNumber: null
          });

          this.ToastService.addErrorToast(
            'Ineligible',
            'WSIB lookup could not verify a valid certificate (invalid, expired, or missing expiration date). Status has been set to Ineligible.'
          );
          return;
        }

        if (!certNumber) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error retrieving the certificate number.',
            { returnError: true }
          );
        }

        this.CanadianWorkersCompensationFormGroup.patchValue({
          CAWCDate: expirationDate,
          CAWCcertNumber: certNumber,
          CAWCStatus: 'Eligible'
        });

        this.ToastService.addSuccessToast(
          'Success',
          'WSIB lookup finished successfully. Status has been set to Eligible.'
        );
      }
    });
  }

  disableEnableExemptCanadianWorkersCompFields(isExempt: boolean = false) {
    ['CAWCNumber', 'CAWCStatus', 'CAWCDate'].forEach((controlName) => {
      const control = this.CanadianWorkersCompensationFormGroup.get(
        controlName
      ) as FormControl;

      if (control) {
        if (isExempt) {
          control.removeValidators([Validators.required]);
        } else {
          control.addValidators([Validators.required]);
        }

        control.updateValueAndValidity();
      }
    });
  }

  ngOnInit(): void {
    if (!this.CanadianWorkersCompensationFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        "Missing Canadian Worker's Compensation form.",
        { returnError: true }
      );
    }

    if (!this.CompanyVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor id.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.CanadianWorkersCompensationType) {
      throw this.ToastService.addErrorToast(
        'Error',
        "Missing Canadian Worker's Compensation type.",
        { returnError: true }
      );
    }

    this.CanadianWorkersCompensationFormGroup.controls.type.patchValue(
      this.CanadianWorkersCompensationType
    );

    const CAWCExemptControl =
      this.CanadianWorkersCompensationFormGroup.controls.CAWCExempt;

    if (CAWCExemptControl) {
      const { value, valueChanges } = CAWCExemptControl;

      this.disableEnableExemptCanadianWorkersCompFields(value);

      valueChanges.subscribe({
        next: (isExempt) => {
          this.disableEnableExemptCanadianWorkersCompFields(isExempt);
        }
      });
    }
  }
}
